import {
  useCallback,
  useLayoutEffect,
  useRef,
  type ChangeEventHandler,
  type KeyboardEventHandler,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

import { isComposing, isEnterKey } from "@/lib/editing";
import { controlBox, type ControlContext } from "./control";
import { FormField } from "./FormField";

export type WrappingTextFieldProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "id" | "rows" | "size"
> & {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  on?: ControlContext;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
};

/**
 * A single-line value that wraps visually and grows to reveal every line.
 *
 * An `<input>` can never wrap, even with whitespace utilities. This uses a
 * one-row textarea for layout while rejecting Return, so the stored value keeps
 * the same single-line contract as `TextField`. The surrounding scroll surface
 * owns overflow; the field never grows an inner scrollbar.
 */
export function WrappingTextField({
  label,
  hint,
  error,
  on = "panel",
  className,
  required,
  value,
  onChange,
  onKeyDown,
  ...rest
}: WrappingTextFieldProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const fit = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    const border = textarea.offsetHeight - textarea.clientHeight;
    textarea.style.height = `${textarea.scrollHeight + border}px`;
  }, []);

  useLayoutEffect(() => {
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [fit, value]);

  return (
    <FormField label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <textarea
          {...rest}
          id={id}
          ref={textareaRef}
          rows={1}
          required={required}
          value={value}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          wrap="soft"
          onChange={onChange}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (
              !event.defaultPrevented &&
              isEnterKey(event) &&
              !isComposing(event.nativeEvent)
            ) {
              event.preventDefault();
            }
          }}
          className={controlBox({
            on,
            invalid,
            className: `min-h-8 resize-none overflow-hidden p-3 ${className ?? ""}`,
          })}
        />
      )}
    </FormField>
  );
}
